import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const TripForm = ({
  trip,
  onSubmit,
  onCancel,
  isSubmitting,
  selectedYear,
  defaultMileageRate,
}) => {
  const isEdit = Boolean(trip?.tripId);
  const originalRate = isEdit ? (trip.mileageRate ?? null) : null;

  const [inputMode, setInputMode] = useState('totalMileage');
  const [formData, setFormData] = useState({
    tripDate: new Date().toISOString().split('T')[0],
    tripOdometerStart: '',
    tripOdometerEnd: '',
    totalMileage: '',
    mileageRate: defaultMileageRate != null ? String(defaultMileageRate) : '',
    notes: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (trip) {
      const dateValue = trip.tripDate
        ? trip.tripDate.split('T')[0]
        : new Date().toISOString().split('T')[0];

      const detectedMode = trip.tripOdometerStart != null ? 'odometer' : 'totalMileage';
      setInputMode(detectedMode);

      setFormData({
        tripDate: dateValue,
        tripOdometerStart: trip.tripOdometerStart != null ? String(trip.tripOdometerStart) : '',
        tripOdometerEnd: trip.tripOdometerEnd != null ? String(trip.tripOdometerEnd) : '',
        totalMileage: detectedMode === 'totalMileage' && trip.totalMileage != null
          ? String(trip.totalMileage)
          : '',
        mileageRate: trip.mileageRate != null ? String(trip.mileageRate) : '',
        notes: trip.notes || '',
      });
    } else {
      setFormData(prev => ({
        ...prev,
        mileageRate: defaultMileageRate != null ? String(defaultMileageRate) : '',
      }));
    }
  }, [trip, defaultMileageRate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'notes' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    setInputMode(newMode);
    setFormData(prev => ({
      ...prev,
      tripOdometerStart: '',
      tripOdometerEnd: '',
      totalMileage: '',
    }));
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.tripDate) {
      newErrors.tripDate = 'Trip date is required';
    } else {
      const dateYear = parseInt(formData.tripDate.split('-')[0], 10);
      if (dateYear !== selectedYear) {
        newErrors.tripDate = `Date must be within ${selectedYear}`;
      }
    }

    if (inputMode === 'odometer') {
      if (!formData.tripOdometerStart && formData.tripOdometerStart !== '0') {
        newErrors.tripOdometerStart = 'Start odometer is required';
      }
      if (!formData.tripOdometerEnd && formData.tripOdometerEnd !== '0') {
        newErrors.tripOdometerEnd = 'End odometer is required';
      }
      if (formData.tripOdometerStart && formData.tripOdometerEnd) {
        const start = parseFloat(formData.tripOdometerStart);
        const end = parseFloat(formData.tripOdometerEnd);
        if (end < start) {
          newErrors.tripOdometerEnd = 'End odometer must be greater than or equal to start';
        }
      }
    } else {
      if (!formData.totalMileage) {
        newErrors.totalMileage = 'Total mileage is required';
      } else if (parseFloat(formData.totalMileage) <= 0) {
        newErrors.totalMileage = 'Mileage must be greater than zero';
      }
    }

    const rateRaw = (formData.mileageRate || '').trim();
    if (rateRaw !== '') {
      const parsedRate = parseFloat(rateRaw);
      if (!Number.isFinite(parsedRate) || parsedRate <= 0) {
        newErrors.mileageRate = 'Rate must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      tripDate: formData.tripDate,
      notes: formData.notes || undefined,
    };

    if (inputMode === 'odometer') {
      payload.tripOdometerStart = parseFloat(formData.tripOdometerStart);
      payload.tripOdometerEnd = parseFloat(formData.tripOdometerEnd);
    } else {
      payload.totalMileage = parseFloat(formData.totalMileage);
    }

    // Mileage rate submission semantics:
    //   Add mode + empty  → omit (backend auto-populates from year default)
    //   Add mode + value  → include as number
    //   Edit mode + empty + originally had rate → include as null (explicit clear)
    //   Edit mode + empty + originally null → omit (no-op)
    //   Edit mode + value → include as number
    const rateRaw = (formData.mileageRate || '').trim();
    if (rateRaw !== '') {
      payload.mileageRate = parseFloat(rateRaw);
    } else if (isEdit && originalRate != null) {
      payload.mileageRate = null;
    }

    onSubmit(payload, trip?.tripId);
  };

  const calculatedMileage = inputMode === 'odometer'
    && formData.tripOdometerStart && formData.tripOdometerEnd
    ? Math.max(0, parseFloat(formData.tripOdometerEnd) - parseFloat(formData.tripOdometerStart))
    : null;

  const minDate = `${selectedYear}-01-01`;
  const maxDate = `${selectedYear}-12-31`;

  return (
    <Box sx={{ p: 2, minWidth: { xs: 300, sm: 400 } }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {trip ? 'Edit Trip' : 'Add New Trip'}
      </Typography>

      <form onSubmit={handleSubmit} aria-label={trip ? 'Edit trip form' : 'Add trip form'}>
        <Stack spacing={2}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
              Input Method
            </FormLabel>
            <RadioGroup
              row
              value={inputMode}
              onChange={handleModeChange}
            >
              <FormControlLabel
                value="odometer"
                control={<Radio size="small" />}
                label="Odometer Reading"
              />
              <FormControlLabel
                value="totalMileage"
                control={<Radio size="small" />}
                label="Total Mileage"
              />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            label="Trip Date"
            name="tripDate"
            type="date"
            value={formData.tripDate}
            onChange={handleInputChange}
            required
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minDate, max: maxDate }}
            error={Boolean(errors.tripDate)}
            helperText={errors.tripDate}
          />

          {inputMode === 'odometer' ? (
            <>
              <TextField
                fullWidth
                label="Odometer Start"
                name="tripOdometerStart"
                type="number"
                value={formData.tripOdometerStart}
                onChange={handleInputChange}
                required
                size="small"
                inputProps={{ min: '0', step: '0.1' }}
                error={Boolean(errors.tripOdometerStart)}
                helperText={errors.tripOdometerStart}
              />
              <TextField
                fullWidth
                label="Odometer End"
                name="tripOdometerEnd"
                type="number"
                value={formData.tripOdometerEnd}
                onChange={handleInputChange}
                required
                size="small"
                inputProps={{ min: '0', step: '0.1' }}
                error={Boolean(errors.tripOdometerEnd)}
                helperText={errors.tripOdometerEnd}
              />
              {calculatedMileage !== null && (
                <Typography variant="body2" color="text.secondary">
                  Calculated mileage: {calculatedMileage.toFixed(1)} mi
                </Typography>
              )}
            </>
          ) : (
            <TextField
              fullWidth
              label="Total Mileage"
              name="totalMileage"
              type="number"
              value={formData.totalMileage}
              onChange={handleInputChange}
              required
              size="small"
              inputProps={{ min: '0.1', step: '0.1' }}
              error={Boolean(errors.totalMileage)}
              helperText={errors.totalMileage}
            />
          )}

          <TextField
            fullWidth
            label="Mileage Rate ($/mi)"
            name="mileageRate"
            type="number"
            value={formData.mileageRate}
            onChange={handleInputChange}
            size="small"
            inputProps={{ min: '0', step: '0.001' }}
            error={Boolean(errors.mileageRate)}
            helperText={errors.mileageRate || (isEdit ? 'Leave blank to clear' : 'Leave blank to use the year default')}
          />

          <TextField
            fullWidth
            label="Notes (Optional)"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            multiline
            rows={2}
            placeholder="Add any notes about this trip"
            size="small"
            inputProps={{ maxLength: 500 }}
            helperText={`${formData.notes.length}/500`}
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
            {onCancel && (
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={onCancel}
                startIcon={<CancelIcon />}
                disabled={isSubmitting}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
              sx={{ textTransform: 'none' }}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default TripForm;
